import { MessageCircle } from 'lucide-react-native';
import { memo, type ReactElement, useCallback, useMemo, useState } from 'react';
import { Badge } from '../../../components/primitives/Badge';
import { Button } from '../../../components/primitives/Button';
import { Select } from '../../../components/primitives/Select';
import { Sheet } from '../../../components/primitives/Sheet';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useModal } from '../../../contexts/ModalContext';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { httpStatus, useSetMarketingConsent } from '../../../hooks/useMarketingConsent';
import { colors } from '../../../theme/colors';
import { type ConsentSource, isConsentSource } from '../../../types/consent';

interface MarketingConsentSectionProps {
  leadId: string;
  marketingOptIn: boolean | null | undefined;
  canManage: boolean;
}

function MarketingConsentSectionComponent({
  leadId,
  marketingOptIn,
  canManage,
}: Readonly<MarketingConsentSectionProps>): ReactElement {
  const { t } = useTranslation();
  const { confirm } = useModal();
  const { showSuccess, showError } = useToast();
  const { mutate, isPending } = useSetMarketingConsent();

  const [grantOpen, setGrantOpen] = useState(false);
  const [source, setSource] = useState<ConsentSource>('manual');

  const sourceOptions = useMemo(
    () => [
      { value: 'manual', label: t('leads.detail.consent.sourceManual') },
      { value: 'form', label: t('leads.detail.consent.sourceForm') },
      { value: 'whatsapp', label: t('leads.detail.consent.sourceWhatsapp') },
      { value: 'web', label: t('leads.detail.consent.sourceWeb') },
    ],
    [t],
  );

  const closeGrant = useCallback(() => setGrantOpen(false), []);
  const handleSourceChange = useCallback((value: string) => {
    if (isConsentSource(value)) setSource(value);
  }, []);

  const handleError = useCallback(
    (error: unknown) => {
      showError(
        t(
          httpStatus(error) === 403
            ? 'leads.detail.marketingConsent.forbidden'
            : 'leads.detail.marketingConsent.error',
        ),
      );
    },
    [showError, t],
  );

  const openGrant = useCallback(() => {
    setSource('manual');
    setGrantOpen(true);
  }, []);

  const submitGrant = useCallback(() => {
    mutate(
      { leadId, granted: true, source },
      {
        onSuccess: () => {
          setGrantOpen(false);
          showSuccess(t('leads.detail.marketingConsent.successGranted'));
        },
        onError: handleError,
      },
    );
  }, [mutate, leadId, source, showSuccess, handleError, t]);

  const handleRevoke = useCallback(async () => {
    const confirmed = await confirm({
      title: t('leads.detail.marketingConsent.revokeTitle'),
      message: t('leads.detail.marketingConsent.revokeBody'),
      confirmLabel: t('leads.detail.marketingConsent.revoke'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    mutate(
      { leadId, granted: false, source: 'manual' },
      {
        onSuccess: () => showSuccess(t('leads.detail.marketingConsent.successRevoked')),
        onError: handleError,
      },
    );
  }, [confirm, mutate, leadId, showSuccess, handleError, t]);

  let badgeColor: 'green' | 'red' | 'amber' = 'amber';
  let badgeLabel = t('leads.detail.marketingConsent.unknown');
  if (marketingOptIn === true) {
    badgeColor = 'green';
    badgeLabel = t('leads.detail.marketingConsent.granted');
  } else if (marketingOptIn === false) {
    badgeColor = 'red';
    badgeLabel = t('leads.detail.marketingConsent.notGranted');
  }

  const actionButton =
    marketingOptIn === true ? (
      <Button
        variant="outline"
        size="xs"
        pill
        onPress={handleRevoke}
        textColor={colors.status.error.main}
        style={{ borderColor: colors.status.error.main }}
        accessibilityLabel={t('leads.detail.marketingConsent.revoke')}
        accessibilityState={{ busy: isPending }}
        disabled={isPending}
        testID="lead_detail_marketing_consent_revoke"
      >
        {t('leads.detail.marketingConsent.revoke')}
      </Button>
    ) : (
      <Button
        variant="outline"
        size="xs"
        pill
        onPress={openGrant}
        accessibilityLabel={t('leads.detail.marketingConsent.grant')}
        accessibilityState={{ busy: isPending }}
        disabled={isPending}
        testID="lead_detail_marketing_consent_grant"
      >
        {t('leads.detail.marketingConsent.grant')}
      </Button>
    );

  return (
    <HStack spacing="md" align="center" testID="lead_detail_marketing_consent_row">
      <MessageCircle size={16} color={colors.text.tertiary} strokeWidth={1.5} />
      <VStack spacing="xs" style={{ flex: 1 }}>
        <Text variant="label" uppercase color={colors.text.tertiary}>
          {t('leads.detail.marketingConsent.title')}
        </Text>
        <HStack spacing="sm" align="center" style={{ flexWrap: 'wrap' }}>
          <Badge color={badgeColor} size="sm" accessibilityLabel={badgeLabel}>
            {badgeLabel}
          </Badge>
          {canManage ? actionButton : null}
        </HStack>
      </VStack>

      <Sheet
        visible={grantOpen}
        onClose={closeGrant}
        title={t('leads.detail.marketingConsent.grantTitle')}
        position="center"
        size="sm"
      >
        <VStack spacing="lg">
          <Text variant="body" color={colors.text.secondary}>
            {t('leads.detail.marketingConsent.grantBody')}
          </Text>

          <VStack spacing="xs">
            <Text variant="caption" color={colors.text.tertiary}>
              {t('leads.detail.marketingConsent.sourceLabel')}
            </Text>
            <Select
              variant="dropdown"
              options={sourceOptions}
              value={source}
              onChange={handleSourceChange}
              testID="marketing-consent-source-select"
            />
          </VStack>

          <Button
            variant="primary"
            onPress={submitGrant}
            accessibilityLabel={t('leads.detail.marketingConsent.submit')}
            accessibilityState={{ busy: isPending }}
            disabled={isPending}
            testID="marketing-consent-submit"
          >
            {isPending
              ? t('leads.detail.marketingConsent.saving')
              : t('leads.detail.marketingConsent.submit')}
          </Button>
        </VStack>
      </Sheet>
    </HStack>
  );
}

export const MarketingConsentSection = memo(MarketingConsentSectionComponent);
